import { GRANULARITY } from '@adtrackify/at-tracking-event-types';

import { getAccountTimezone } from '@/store/account/timezone-cache';
import { attributionParams } from '@/features/dashboard/constants/url-hydration-constants';
import {
  getCompareDateRange,
  getDateRangeFromPreset,
} from '@/lib/helpers/dates/date-ranges';
import type {
  DateRangeState,
} from '@/lib/types/date-range-types';

import { DEFAULT_COMPARE_PRESET } from '../../constants/dashboard-filter-constants';
import { ViewState } from '../../types/dashboard-filter-types';
import { getDefaultViewState } from '../../helpers/get-default-view-state';

import { validateGranularity } from '../../helpers/filters/granularity-helpers';
import type { ImmerSet, StoreGet, ViewHydrationActions } from '../../types/filter-actions-types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function setViewField(view: ViewState, key: string, value: unknown): void {
  Object.assign(view, { [key]: value });
}

function hasAttributionUrlParams(): boolean {
  if (typeof window === 'undefined') return false;
  const url = new URL(window.location.href);
  return attributionParams.some(param => url.searchParams.has(param));
}

function getExternalField(obj: Partial<ViewState>, key: string): unknown {
  return Reflect.get(obj, key);
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export function createViewHydrationActions(
  set: ImmerSet,
  get: StoreGet
): ViewHydrationActions {
  return {
    updateFromUrlParams: (viewId, params) => {
      let needsRecalculation = false;

      set(state => {
        if (!state.views[viewId]) {
          state.views[viewId] = getDefaultViewState();
        }
        const currentView = state.views[viewId];

        const hasNonCustomPreset = params.preset && params.preset !== 'custom';
        const hasNonCustomComparePreset =
          params.comparePreset && params.comparePreset !== 'custom';

        Object.keys(params).forEach(key => {
          if (key === 'granularity') return;
          if (key === 'dateRange' && hasNonCustomPreset) return;
          if (key === 'compareDateRange' && hasNonCustomComparePreset) return;

          const value: unknown = Reflect.get(params, key);
          if (value !== undefined) {
            if (key === 'isCompare') {
              currentView['enableCompareDate'] =
                value === 'true' || value === true;
            } else if (key === 'includeToday') {
              currentView['includeToday'] = value === 'true' || value === true;
            } else if (key === 'attributionWindow' && typeof value === 'string') {
              setViewField(currentView, key, parseInt(value, 10));
            } else if (
              key === 'compareDateRange' &&
              !(value as DateRangeState | null)?.start &&
              value !== null
            ) {
              setViewField(currentView, key, null);
            } else {
              setViewField(currentView, key, value);
            }
          }
        });

        // Track if we need recalculation (will do it after set completes)
        needsRecalculation = !!(params.preset || params.comparePreset);

        // Now handle granularity with validation against the recalculated dateRange
        if ('granularity' in params && params.granularity !== undefined) {
          currentView.granularity = validateGranularity(
            params.granularity as GRANULARITY,
            currentView.dateRange,
            'from URL params is invalid for the current date range (> 14 days)'
          );
        }
      });

      // Recalculate AFTER the set completes so it has the updated state
      if (needsRecalculation) {
        get().recalculateDateRanges(viewId);
      }
    },

    initializeViewFromExternalStore: (viewId, externalState) => {
      set(state => {
        if (!state.views[viewId]) {
          state.views[viewId] = getDefaultViewState();
        }

        const timezone = getAccountTimezone();
        const currentView = state.views[viewId];

        const hasUrlParams = hasAttributionUrlParams();

        if (hasUrlParams) {
          if (externalState.selectedMetric) {
            currentView.selectedMetric = externalState.selectedMetric;
          }
          if ('refundDateAttribution' in (externalState ?? {})) {
            currentView.refundDateAttribution = externalState.refundDateAttribution;
          }
          return;
        }

        const updates: Partial<ViewState> = {};
        let requestedGranularity: GRANULARITY | undefined;

        Object?.keys?.(externalState ?? {})?.forEach(key => {
          if (key === 'primaryDateRange') {
            updates.dateRange = getExternalField(externalState, key) as DateRangeState;
          } else if (key === 'granularity') {
            requestedGranularity = getExternalField(externalState, key) as GRANULARITY;
          } else if (getExternalField(externalState, key) !== undefined) {
            setViewField(updates as ViewState, key, getExternalField(externalState, key));
          }
        });

        // If an external payload provides an explicit date range without a preset, treat it
        // as custom to prevent preset/dateRange UI-query drift.
        if (updates.dateRange && !updates.preset) {
          updates.preset = 'custom';
        }

        let basePrimaryRange = updates.dateRange || currentView.dateRange;
        if (updates.preset && updates.preset !== 'custom') {
          const includeCurrentPeriod = updates.includeToday ?? currentView.includeToday ?? true;
          updates.dateRange = getDateRangeFromPreset(updates.preset, timezone, includeCurrentPeriod);
          basePrimaryRange = updates.dateRange;
        }

        // Apply other updates first
        Object.keys(updates).forEach(key => {
          if (key in currentView) {
            setViewField(currentView, key, getExternalField(updates, key));
          }
        });

        // Now validate and set granularity using the potentially updated dateRange
        if (requestedGranularity) {
          currentView.granularity = validateGranularity(
            requestedGranularity,
            currentView.dateRange,
            'from external store is invalid for the date range (> 14 days)'
          );
        }

        // Recalculate compare date range
        const enableCompare = updates.enableCompareDate ?? currentView.enableCompareDate;
        const comparePreset = updates.comparePreset ?? currentView.comparePreset;

        if (enableCompare) {
          if (comparePreset && comparePreset !== 'custom') {
            currentView.compareDateRange = getCompareDateRange(
              basePrimaryRange,
              comparePreset,
              timezone
            );
            currentView.comparePreset = comparePreset;
          } else if (updates.compareDateRange) {
            currentView.compareDateRange = updates.compareDateRange;
            currentView.comparePreset = 'custom';
          } else {
            currentView.compareDateRange = getCompareDateRange(
              basePrimaryRange,
              'samePeriodLastYear',
              timezone
            );
            currentView.comparePreset = 'samePeriodLastYear';
          }
        } else {
          currentView.compareDateRange = null;
          currentView.comparePreset = DEFAULT_COMPARE_PRESET;
        }

        if ('refundDateAttribution' in (externalState ?? {})) {
          currentView.refundDateAttribution = externalState.refundDateAttribution;
        }
      });
    },
  };
}
